if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          desktopPrevious: this.querySelector('[data-gallery-desktop-previous]'),
          desktopNext: this.querySelector('[data-gallery-desktop-next]'),
          viewerPrevious: this.querySelector('[id^="GalleryViewer"] button[name="previous"]'),
          viewerNext: this.querySelector('[id^="GalleryViewer"] button[name="next"]'),
          viewerCurrent: this.querySelector('[id^="GalleryViewer"] .slider-counter--current'),
          viewerTotal: this.querySelector('[id^="GalleryViewer"] .slider-counter--total'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer?.addEventListener('slideChanged', (event) => {
          if (this.dataset.fixedFrame === 'true') {
            // The base slider assumes equal widths; use actual photo offsets.
            event.stopImmediatePropagation();
            this.updateDesktopNavigation();
            return;
          }
          this.updateMobileGalleryHeight(event.detail.currentElement);
        });
        if (this.dataset.fixedFrame === 'true') {
          this.elements.viewer?.querySelector('.product__media-list')?.addEventListener('scroll', () => {
            this.updateDesktopNavigation();
          }, { passive: true });
        }
        this.elements.desktopPrevious?.addEventListener('click', () => this.switchDesktopMedia(-1));
        this.elements.desktopNext?.addEventListener('click', () => this.switchDesktopMedia(1));
        this.elements.viewerPrevious?.addEventListener('click', (event) => this.handleViewerNavigation(event, -1), {
          capture: true,
        });
        this.elements.viewerNext?.addEventListener('click', (event) => this.handleViewerNavigation(event, 1), {
          capture: true,
        });
        this.mql.addEventListener('change', () => {
          this.updateDesktopNavigation();
          this.updateMobileGalleryHeight();
        });
        this.updateDesktopNavigation();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.fixedFrame === 'true') {
          this.fixedFrameObserver ||= new ResizeObserver(() => {
            cancelAnimationFrame(this.navigationFrame);
            this.navigationFrame = requestAnimationFrame(() => {
              this.matchProductPanelHeight();
              this.updateDesktopNavigation();
            });
          });
          this.fixedFrameObserver.observe(this.elements.viewer.querySelector('.product__media-list'));
          const product = this.closest('.product');
          [
            product?.querySelector('.product__info-wrapper'),
            product?.querySelector('.product__info-container'),
            product?.querySelector('.mural-cta-card__buy, .wp-cta-card__buy'),
            this.closest('.product__media-wrapper'),
          ].forEach((element) => {
            if (element) this.fixedFrameObserver.observe(element);
          });
          return;
        }
        this.mobileResizeObserver ||= new ResizeObserver(() => {
          cancelAnimationFrame(this.mobileHeightFrame);
          this.mobileHeightFrame = requestAnimationFrame(() => this.updateMobileGalleryHeight());
        });
        this.updateMobileGalleryHeight();
      }

      disconnectedCallback() {
        this.fixedFrameObserver?.disconnect();
        cancelAnimationFrame(this.navigationFrame);
        this.mobileResizeObserver?.disconnect();
        this.observedMobileMedia = null;
        cancelAnimationFrame(this.mobileHeightFrame);
      }

      matchProductPanelHeight() {
        if (!window.matchMedia('(min-width: 990px)').matches) {
          this.style.removeProperty('--gallery-desktop-height');
          return;
        }
        const wrapper = this.closest('.product__media-wrapper');
        const info = this.closest('.product')?.querySelector('.product__info-wrapper');
        const list = this.elements.viewer.querySelector('.product__media-list');
        if (!wrapper || !info || !list) return;
        // Align the photo itself with the gray purchase card's bottom edge,
        // immediately below shipping, excluding perks and gallery controls.
        const purchaseCard = info.querySelector('.mural-cta-card__buy, .wp-cta-card__buy');
        const stickyPanel = info.querySelector('.product__info-container');
        // Use the panel's position in normal flow so sticky scrolling never
        // changes photo sizes, including after a resize or material change.
        const stickyOffset = stickyPanel
          ? info.getBoundingClientRect().top - stickyPanel.getBoundingClientRect().top
          : 0;
        const extraHeight = wrapper.getBoundingClientRect().height - list.getBoundingClientRect().height;
        const height = Math.max(1, purchaseCard
          ? purchaseCard.getBoundingClientRect().bottom - list.getBoundingClientRect().top + stickyOffset
          : info.getBoundingClientRect().height - extraHeight);
        const previous = parseFloat(this.style.getPropertyValue('--gallery-desktop-height'));
        if (!Number.isFinite(previous) || Math.abs(previous - height) > 0.5) {
          this.style.setProperty('--gallery-desktop-height', `${height}px`);
        }
      }

      updateMobileGalleryHeight(media) {
        const list = this.elements.viewer?.querySelector('.product__media-list');
        if (!list) return;
        if (media) this.currentMobileMedia = media;
        if (this.dataset.fixedFrame === 'true' || this.mql.matches || this.closest('.product--mobile-columns')) {
          list.style.removeProperty('--mobile-gallery-height');
          this.mobileResizeObserver?.disconnect();
          this.observedMobileMedia = null;
          return;
        }
        const activeMedia = this.currentMobileMedia?.isConnected
          ? this.currentMobileMedia
          : list.querySelector('.is-active') || list.querySelector('[data-media-id]');
        if (!activeMedia) return;
        if (activeMedia !== this.observedMobileMedia && this.mobileResizeObserver) {
          this.mobileResizeObserver.disconnect();
          this.mobileResizeObserver.observe(activeMedia);
          this.observedMobileMedia = activeMedia;
        }
        const height = Math.ceil(activeMedia.getBoundingClientRect().height);
        if (height > 0) list.style.setProperty('--mobile-gallery-height', `${height}px`);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      switchDesktopMedia(direction) {
        const mediaItems = [...this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')];
        if (mediaItems.length < 2) return;
        const activeIndex = Math.max(0, mediaItems.findIndex((item) => item.classList.contains('is-active')));
        const nextIndex = Math.min(mediaItems.length - 1, Math.max(0, activeIndex + direction));
        if (nextIndex === activeIndex) return;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      handleViewerNavigation(event, direction) {
        if (this.dataset.fixedFrame === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          const list = this.elements.viewer.querySelector('.product__media-list');
          const items = [...list.querySelectorAll('[data-media-id]')];
          const start = items[0]?.offsetLeft || 0;
          const max = Math.max(0, list.scrollWidth - list.clientWidth);
          const stops = [...new Set([0, ...items.map((item) => Math.min(max, item.offsetLeft - start)), max])];
          const target = direction > 0
            ? stops.find((position) => position > list.scrollLeft + 2) ?? max
            : stops.reverse().find((position) => position < list.scrollLeft - 2) ?? 0;
          list.scrollTo({ left: target });
          return;
        }
        if (!this.mql.matches) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.switchDesktopMedia(direction);
      }

      updateDesktopNavigation() {
        const mediaItems = [...this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')];
        const activeIndex = Math.max(0, mediaItems.findIndex((item) => item.classList.contains('is-active')));
        if (this.dataset.fixedFrame === 'true') {
          const list = this.elements.viewer.querySelector('.product__media-list');
          const start = mediaItems[0]?.offsetLeft || 0;
          const max = Math.max(0, list.scrollWidth - list.clientWidth);
          let currentIndex = 0;
          let distance = Infinity;
          mediaItems.forEach((item, index) => {
            const delta = Math.abs(Math.min(max, item.offsetLeft - start) - list.scrollLeft);
            if (delta < distance) {
              distance = delta;
              currentIndex = index;
            }
          });
          mediaItems.forEach((item, index) => item.classList.toggle('is-active', index === currentIndex));
          if (this.elements.viewerPrevious) this.elements.viewerPrevious.disabled = list.scrollLeft <= 1;
          if (this.elements.viewerNext) this.elements.viewerNext.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
          if (this.elements.viewerCurrent) this.elements.viewerCurrent.textContent = currentIndex + 1;
          if (this.elements.viewerTotal) this.elements.viewerTotal.textContent = mediaItems.length;
          return;
        }
        if (this.elements.desktopPrevious) this.elements.desktopPrevious.disabled = activeIndex <= 0;
        if (this.elements.desktopNext) this.elements.desktopNext.disabled = activeIndex >= mediaItems.length - 1;
        if (!this.mql.matches) return;
        if (this.elements.viewerPrevious) this.elements.viewerPrevious.disabled = activeIndex <= 0;
        if (this.elements.viewerNext) this.elements.viewerNext.disabled = activeIndex >= mediaItems.length - 1;
        if (this.elements.viewerCurrent) this.elements.viewerCurrent.textContent = activeIndex + 1;
        if (this.elements.viewerTotal) this.elements.viewerTotal.textContent = mediaItems.length;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateMobileGalleryHeight(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.dataset.fixedFrame === 'true') {
            const list = activeMedia.parentElement;
            list.scrollTo({ left: activeMedia.offsetLeft - list.querySelector('[data-media-id]').offsetLeft });
            return;
          }
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Desktop thumbnail layouts replace the active item in place. Page
          // scrolling here makes gallery navigation feel like a layout jump.
          if (this.mql.matches) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateDesktopNavigation();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
